/*
 * Purpose: Unified app shell — primary nav, live pill, outlet — P0
 * Last modified: 2026-07-10 | Agent
 * Completeness: 98
 */
import { useRef } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { BrandMark } from '@/components/brand/BrandMark';
import { AuthAccountMenu } from '@/components/auth/AuthAccountMenu';
import { AuthMockBanner } from '@/components/auth/AuthMockBanner';
import { GuestModeBanner } from '@/components/auth/GuestModeBanner';
import { PrimaryNav } from '@/components/shell/PrimaryNav';
import { MobileNav } from '@/components/shell/MobileNav';
import { LiveRunsPill } from '@/components/shell/LiveRunsPill';
import { FlowQuerySync } from '@/components/shell/FlowQuerySync';
import { TenantStorageSync } from '@/components/shell/TenantStorageSync';
import { GlobalAppShortcuts } from '@/components/shell/GlobalAppShortcuts';
import { ScheduleRunner } from '@/components/shell/ScheduleRunner';
import { ConnectorFixModal } from '@/components/connectors/ConnectorFixModal';
import { AppFooter } from '@/components/shell/AppFooter';
import { AppVersionBadge } from '@/components/shell/AppVersionBadge';
import { useAppTopOffset } from '@/hooks/use-app-top-offset';
import { PRODUCT_NAME } from '@/lib/productTerms';

export function AppShell() {
  const topChromeRef = useRef<HTMLDivElement>(null);
  useAppTopOffset(topChromeRef);

  return (
    <div className="min-h-screen flex flex-col bg-background ui-page-hierarchy">
      <FlowQuerySync />
      <TenantStorageSync />
      <ScheduleRunner />
      <GlobalAppShortcuts />
      <div
        ref={topChromeRef}
        data-app-top-chrome
        className="sticky top-0 z-30 shrink-0 bg-background/90 backdrop-blur-sm will-change-transform"
      >
        <header className="border-b border-border/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 shrink-0 min-w-0">
              <BrandMark size={26} className="shrink-0" ghostAccent="darker" />
              <span className="ab-brand-wordmark text-sm text-foreground hidden sm:inline truncate">
                {PRODUCT_NAME}
              </span>
              <AppVersionBadge className="hidden md:inline-flex" />
            </Link>
            <PrimaryNav />
            <div className="flex items-center gap-2 ml-auto shrink-0">
              <LiveRunsPill />
              <AuthAccountMenu />
            </div>
          </div>
        </header>
        <AuthMockBanner />
        <GuestModeBanner />
      </div>
      <main className="flex-1 min-h-0 flex flex-col pb-[calc(52px+env(safe-area-inset-bottom))] md:pb-0">
        <Outlet />
      </main>
      <MobileNav />
      <AppFooter />
      <ConnectorFixModal />
    </div>
  );
}

export default AppShell;
